import { useEffect, useState } from "react";
import { Wallet as WalletIcon, Clock, ArrowUpRight, ArrowDownRight, ShieldCheck, TrendingUp } from "lucide-react";
import { api, inr, fmtDateTime } from "@/lib/api";
import { Skeleton } from "@/components/common";

const TYPE_META = {
  EARN_PENDING: { label: "Reward pending", color: "text-amber-600", icon: Clock },
  EARN_CONFIRMED: { label: "Reward credited", color: "text-emerald-600", icon: ArrowUpRight },
  WALLET_USED: { label: "Used for payment", color: "text-slate-600", icon: ArrowDownRight },
  REVERSAL: { label: "Reward voided", color: "text-red-500", icon: ArrowDownRight },
};
const STATUS_PILL = {
  PENDING: "bg-amber-100 text-amber-700",
  CONFIRMED: "bg-emerald-100 text-emerald-700",
  USED: "bg-slate-100 text-slate-600",
  REJECTED: "bg-red-100 text-red-600",
};

export default function Wallet() {
  const [d, setD] = useState(null);

  useEffect(() => { api.get("/wallet").then((r) => setD(r.data)).catch(() => setD({ transactions: [] })); }, []);

  if (!d) return <div className="space-y-4"><Skeleton className="h-40" /><Skeleton className="h-64" /></div>;

  const stat = (label, value, sub, cls) => (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <p className="text-xs font-medium text-slate-500">{label}</p>
      <p className={`font-display text-2xl font-bold ${cls || "text-[#0B132B]"}`}>{inr(value)}</p>
      {sub && <p className="mt-0.5 text-[11px] text-slate-400">{sub}</p>}
    </div>
  );

  return (
    <div className="mx-auto max-w-4xl space-y-6" data-testid="wallet-page">
      <div className="relative mx-auto w-full max-w-[900px]" style={{ containerType: "inline-size" }} data-testid="wallet-banner">
        <img src="/brand/penguin-wallet.webp" alt="ZRo Pe Wallet — your rewards grow here" className="block h-auto w-full" />
        <div className="absolute" style={{ left: "5.5%", top: "44%", width: "34%" }}>
          <p className="font-semibold text-[#1D4ED8]" style={{ fontSize: "clamp(9px,2.3cqw,18px)", lineHeight: 1 }}>Available Balance</p>
          <p className="font-display font-bold text-[#0B2E6B]" data-testid="wallet-available-balance" style={{ fontSize: "clamp(18px,5.4cqw,52px)", lineHeight: 1.1 }}>{inr(d.available_balance)}</p>
          <p className="text-[#2563EB]" data-testid="wallet-pending-balance" style={{ fontSize: "clamp(9px,1.9cqw,14px)", lineHeight: 1.2, marginTop: "3%" }}>{inr(d.pending_balance)} pending confirmation</p>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        {stat("Available", d.available_balance, "Ready to use", "text-[#14B8A6]")}
        {stat("Pending", d.pending_balance, "Awaiting lender", "text-amber-600")}
        {stat("Total earned", d.total_earned, "Lifetime credited")}
      </div>

      {/* Ledger */}
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center gap-2 border-b border-slate-100 px-5 py-3"><TrendingUp size={16} className="text-[#14B8A6]" /><h2 className="font-display font-bold text-[#0B132B]">Reward Ledger</h2></div>
        {(!d.transactions || d.transactions.length === 0) ? (
          <div className="p-8 text-center text-sm text-slate-400" data-testid="wallet-empty">No wallet activity yet. Make a payment to start earning rewards.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {d.transactions.map((t) => {
              const M = TYPE_META[t.type] || { label: t.type, color: "text-slate-600", icon: WalletIcon };
              const Icon = M.icon;
              return (
                <div key={t.wallet_txn_id || t.id} className="flex items-center justify-between px-5 py-3" data-testid={`wallet-txn-${t.wallet_txn_id}`}>
                  <div className="flex items-center gap-3">
                    <span className={`grid h-9 w-9 place-items-center rounded-lg bg-slate-100 ${M.color}`}><Icon size={16} /></span>
                    <div>
                      <p className="text-sm font-semibold text-[#0B132B]">{M.label}</p>
                      <p className="text-[11px] text-slate-400">{t.loan_number ? `${t.loan_number} • ` : ""}{t.payment_txn_id} • {fmtDateTime(t.timestamp)}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`font-display font-bold ${t.amount < 0 ? "text-slate-600" : "text-emerald-600"}`}>{t.amount < 0 ? "−" : "+"}{inr(Math.abs(t.amount))}</p>
                    <span className={`mt-0.5 inline-block rounded px-1.5 py-0.5 text-[10px] font-bold ${STATUS_PILL[t.status] || "bg-slate-100 text-slate-500"}`}>{t.status}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
